import type { BatchRemoveFromProjectInput, Project, ProjectFolder, ProjectListPublicResponse, RemoveFromProjectInput, SaveToProjectData } from '../services';
import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export const SaveToProjectApi = Symbol('SaveToProjectApi');

export interface ProjectListParams {
    page: number;
    limit: number;
}

export interface ProjectListResponse {
    result: ProjectFolder[];
    total: number;
    page: number;
    limit: number;
    show_recommend_items: boolean;
}

export interface SaveToProjectApi {
    getFolderList(): Promise<ProjectListPublicResponse[]>;
    getFolderListWithPagination(params: ProjectListParams): Promise<ProjectListResponse>;
    createFolder(data: Project): Promise<ProjectFolder>;
    editFolder(data: ProjectFolder): Promise<ProjectFolder>;
    removeFolder(id: string): Promise<boolean>;
    addToFolder(data: SaveToProjectData): Promise<void>;
    removeFromProject(data: RemoveFromProjectInput): Promise<void>;
    batchRemoveFromProject(data: BatchRemoveFromProjectInput): Promise<void>;
}

@injectable()
export class SaveToProjectApiImpl extends PsHttpService implements SaveToProjectApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/work/project`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    getFolderList() {
        return this.get<PatSnapCommonResponseData<ProjectListPublicResponse[]>>({
            url: '/fetch-all',
        }).then((res) => {
            return res.data as ProjectListPublicResponse[];
        })
    }

    getFolderListWithPagination(params: ProjectListParams) {
        return this.post<PatSnapCommonResponseData<ProjectListResponse>>({
            url: '/list',
            data: params,
        }).then((res) => {
            return res.data as ProjectListResponse;
        })
    }

    createFolder(data: Project) {
        return this.post<PatSnapCommonResponseData<ProjectFolder>>({
            url: '/create',
            data,
        }).then((res) => {
            return res.data as ProjectFolder;
        });
    }

    editFolder(data: ProjectFolder) {
        return this.post<PatSnapCommonResponseData<ProjectFolder>>({
            url: '/update',
            data,
        }).then((res) => {
            return res.data as ProjectFolder;
        });
    }

    removeFolder(id: string) {
        return this.delete<PatSnapCommonResponseData<boolean>>({
            url: `/${id}`,
        }).then((res) => {
            return res.data as boolean
        });
    }

    addToFolder(data: SaveToProjectData) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/item/add',
            data,
        }).then((res) => {
            return res.data as void;
        });
    }

    removeFromProject(data: RemoveFromProjectInput) {
        return this.delete<PatSnapCommonResponseData<void>>({
            url: '/item/delete',
            data: {
                work_project_id: data.business_id,
                item_id: data.business_node_id,
            },
        }).then((res) => {
            return res.data as void;
        });
    }

    batchRemoveFromProject(data: BatchRemoveFromProjectInput) {
        return this.delete<PatSnapCommonResponseData<void>>({
            url: '/item/batch-delete',
            data: {
                work_project_id: data.business_id,
                item_ids: data.business_node_ids,
            },
        }).then((res) => {
            return res.data as void;
        });
    }
}
